import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { lazyWithPreload } from './lazyWithPreload';
import App from './App';
import Layout from './components/Layout';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import RedirectWithoutTrailingSlash from './components/RedirectWithoutTrailingSlash';

// Admin layout (with AuthProvider + Supabase auth) is only loaded when the
// user navigates to a /login or /admin route. Public pages don't pull in
// supabase-vendor at all.
const AdminLayout = lazyWithPreload(() => import('./components/AdminLayout'));

// Code-split route components to keep initial bundle small.
const IndividualCounselling = lazyWithPreload(() => import('./pages/IndividualCounselling'));
const CouplesTherapy = lazyWithPreload(() => import('./pages/CouplesTherapy'));
const ReikiTherapy = lazyWithPreload(() => import('./pages/ReikiTherapy'));
const IcbcBilling = lazyWithPreload(() => import('./pages/IcbcBilling'));
const Telehealth = lazyWithPreload(() => import('./pages/Telehealth'));
const OxygenTherapy = lazyWithPreload(() => import('./pages/OxygenTherapy'));
const IndianHeadMassage = lazyWithPreload(() => import('./pages/IndianHeadMassage'));
const ChildCounselling = lazyWithPreload(() => import('./pages/ChildCounselling'));
const MentalHealth = lazyWithPreload(() => import('./pages/MentalHealth'));
const Anxiety = lazyWithPreload(() => import('./pages/therapies/Anxiety'));
const Depression = lazyWithPreload(() => import('./pages/therapies/Depression'));
const PostTraumaticStress = lazyWithPreload(() => import('./pages/therapies/PostTraumaticStress'));
const AngerManagement = lazyWithPreload(() => import('./pages/therapies/AngerManagement'));
const WomensIssues = lazyWithPreload(() => import('./pages/therapies/WomensIssues'));
const GriefTherapy = lazyWithPreload(() => import('./pages/therapies/GriefTherapy'));
const FamilyTherapy = lazyWithPreload(() => import('./pages/therapies/FamilyTherapy'));
const LGBTQCounselling = lazyWithPreload(() => import('./pages/therapies/LGBTQCounselling'));
const Contact = lazyWithPreload(() => import('./pages/Contact'));
const About = lazyWithPreload(() => import('./pages/About'));
const Services = lazyWithPreload(() => import('./pages/Services'));
const Therapies = lazyWithPreload(() => import('./pages/Therapies'));
const Blog = lazyWithPreload(() => import('./pages/Blog'));
const BlogPost = lazyWithPreload(() => import('./pages/BlogPost'));
const Career = lazyWithPreload(() => import('./pages/Career'));
const Developers = lazyWithPreload(() => import('./pages/Developers'));
const CounsellorBio = lazyWithPreload(() => import('./components/CounsellorBio'));
const Login = lazyWithPreload(() => import('./pages/Login'));
const BlogAdmin = lazyWithPreload(() => import('./supabase/BlogAdmin'));
const MetaEditor = lazyWithPreload(() => import('./supabase/MetaEditor'));


export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Layout />,
    errorElement: <NotFound />,
    children: [
      {
        path: '*/',
        element: <RedirectWithoutTrailingSlash />,
      },
      {
        index: true,
        element: <App />,
      },
      {
        // Auth-required routes: AdminLayout lazy-loads the AuthProvider
        // (and therefore @supabase) only when one of these paths is hit.
        element: <AdminLayout />,
        children: [
          {
            path: 'login',
            element: <Login />,
          },
          {
            path: 'admin',
            element: (
              <ProtectedRoute>
                <BlogAdmin />
              </ProtectedRoute>
            ),
          },
          {
            path: 'admin/new',
            element: (
              <ProtectedRoute>
                <BlogAdmin />
              </ProtectedRoute>
            ),
          },
          {
            path: 'admin/:id',
            element: (
              <ProtectedRoute>
                <BlogAdmin />
              </ProtectedRoute>
            ),
          },
          {
            path: 'admin/meta',
            element: (
              <ProtectedRoute>
                <MetaEditor />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'blog',
        element: <Blog />,
      },
      {
        path: 'blog/:slug',
        element: <BlogPost />,
      },
      {
        path: 'meet-our-counsellors',
        element: <About />,
      },
      {
        path: 'career',
        element: <Career />,
      },
      {
        path: 'developers',
        element: <Developers />,
      },
      {
        path: 'contact-us',
        element: <Contact />,
      },
      {
        path: 'services',
        element: <Services />,
      },
      {
        path: 'other-wellness-therapies',
        element: <Therapies />,
      },
      {
        path: 'holy-fire-reiki-therapy-surrey',
        element: <ReikiTherapy />,
      },
      {
        path: 'icbc-coverage-and-other-coverage',
        element: <IcbcBilling />,
      },
      {
        path: 'tele-health-system',
        element: <Telehealth />,
      },
      {
        path: 'anxiety-stress-management-counselor-in-surrey',
        element: <Anxiety />,
      },
      {
        path: 'depression-treatment-counsellors-in-surrey',
        element: <Depression />,
      },
      {
        path: 'post-traumatic-stress-disorder-counsellors',
        element: <PostTraumaticStress />,
      },
      {
        path: 'anger-management-counselling-surrey',
        element: <AngerManagement />,
      },
      {
        path: 'womens-issues-counsellors-in-surrey',
        element: <WomensIssues />,
      },
      {
        path: 'grief-counselling-support-surrey',
        element: <GriefTherapy />,
      },
      {
        path: 'family-therapy-counselling-services-in-surrey',
        element: <FamilyTherapy />,
      },
      {
        path: 'lgbtq-counselling',
        element: <LGBTQCounselling />,
      },
      {
        path: 'lgbt-adults',
        element: <Navigate to="/lgbtq-counselling" replace={true} />,
      },
      {
        path: 'lgbt-teens',
        element: <Navigate to="/lgbtq-counselling" replace={true} />,
      },
      {
        path: 'individual-counselling-therapy-surrey',
        element: <IndividualCounselling />,
      },
      {
        path: 'couples-therapy-marriage-counselling-surrey',
        element: <CouplesTherapy />,
      },
      {
        path: 'mental-health-counselling-surrey',
        element: <MentalHealth />,
      },
      {
        path: 'oxygen-therapy-surrey',
        element: <OxygenTherapy />,
      },
      {
        path: 'indian-head-massage-therapy-surrey',
        element: <IndianHeadMassage />,
      },
      {
        path: 'child-counselling-services-surrey',
        element: <ChildCounselling />,
      },
      {
        path: 'individual-counselling',
        element: <Navigate to="/individual-counselling-therapy-surrey" replace={true} />,
      },
      {
        path: 'couples-therapy',
        element: <Navigate to="/couples-therapy-marriage-counselling-surrey" replace={true} />,
      },
      {
        path: 'oxygen-therapy',
        element: <Navigate to="/oxygen-therapy-surrey" replace={true} />,
      },
      {
        path: 'indian-head-massage',
        element: <Navigate to="/indian-head-massage-therapy-surrey" replace={true} />,
      },
      {
        path: 'child-counselling',
        element: <Navigate to="/child-counselling-services-surrey" replace={true} />,
      },
      {
        path: 'mental-health',
        element: <Navigate to="/mental-health-counselling-surrey" replace={true} />,
      },
      {
        path: 'grief-counselling',
        element: <Navigate to="/grief-counselling-support-surrey" replace={true} />,
      },
      {
        path: 'grief-counselling-in-surrey',
        element: <Navigate to="/grief-counselling-support-surrey" replace={true} />,
      },
      {
        path: 'grief-therapy',
        element: <Navigate to="/grief-counselling-support-surrey" replace={true} />,
      },
      {
        path: 'depression',
        element: <Navigate to="/depression-treatment-counsellors-in-surrey" replace={true} />,
      },
      {
        path: 'anxiety',
        element: <Navigate to="/anxiety-stress-management-counselor-in-surrey" replace={true} />,
      },
      {
        path: 'anger-management',
        element: <Navigate to="/anger-management-counselling-surrey" replace={true} />,
      },
      {
        path: 'about-telehealth',
        element: <Navigate to="/tele-health-system" replace={true} />,
      },
      {
        path: 'meeting-in-person-during-the-covid-19',
        element: <Navigate to="/tele-health-system" replace={true} />,
      },
      {
        path: 'metro-counselling-your-leading-solution-for-anger-management-in-vancouver',
        element: <Navigate to="/anger-management-counselling-surrey" replace={true} />,
      },
      {
        path: 'counsellor/:counsellorId',
        element: <CounsellorBio />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
];

const router = createBrowserRouter(routes);

export default router;
